import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ClipboardCheck, Camera, Plus, ChevronDown, ChevronUp, Check, X, 
  AlertTriangle, Clock, User, MapPin, FileText, Download, Mail,
  CheckCircle2, XCircle, AlertCircle, Image, Trash2, Save, Eye,
  Calendar, Building, Sparkles, Search, Filter, RotateCcw,
  Edit, Trash, HardDrive, RefreshCw, Zap, PenTool, ArrowLeftRight,
  BarChart3, Navigation, Send, TrendingUp, TrendingDown, Activity
} from 'lucide-react';
import { IconTile, TINT } from '../components/ui/IconTile';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import jsPDF from 'jspdf';
import inspectionsApi from '../services/inspectionsApi';
import { photoStorage, STORAGE_LIMIT } from '../services/photoStorage';
import InspectionCameraModal from '../components/inspections/InspectionCameraModal';
import SignatureModal from '../components/inspections/SignatureModal';
import InspectionModal from '../components/inspections/InspectionModal';
import InspectionAnalyticsTab from '../components/inspections/InspectionAnalyticsTab';
import InspectionList from '../components/inspections/InspectionList';
import {
  calculateProgress as calcProgress,
  calculateScore as calcScore,
  getSectionCounts as getCountsForCategory,
  scoreToRating,
} from '../utils/inspectionScoring';
import { getAnalyticsData as buildAnalytics } from '../utils/inspectionAnalytics';

const CleaningInspection = () => {
  const { user } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const fileInputRef = useRef(null);
  const [currentPhotoItem, setCurrentPhotoItem] = useState(null);
  const [storageInfo, setStorageInfo] = useState({ size: 0, count: 0 });
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [activeTab, setActiveTab] = useState('queue'); // queue, analytics
  
  // Quick Win Features State
  const [showCriticalOnly, setShowCriticalOnly] = useState(false); // Critical items filter
  const [focusedItemId, setFocusedItemId] = useState(null); // For keyboard navigation
  
  // Camera state
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [isCompressing, setIsCompressing] = useState(false);
  const [photoMode, setPhotoMode] = useState('standard'); // standard, before, after
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const compressionCanvasRef = useRef(null);
  
  // Signature capture state
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureType, setSignatureType] = useState('customer'); // customer, inspector
  const signatureRef = useRef(null);
  
  // Geolocation state
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  // Image compression settings - optimized for quality and size
  const IMAGE_CONFIG = {
    maxWidth: 1200,      // Increased for better quality
    maxHeight: 1200,     // Increased for better quality
    quality: 0.7,        // Better quality
    mimeType: 'image/jpeg'
  };

  // Get current geolocation
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        });
        setLocationError(null);
      },
      (error) => {
        setLocationError(error.message);
        console.error('Geolocation error:', error);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Calculate analytics data — pure data crunch delegated to a utility so
  // the page component stays focused on rendering. Memoized on `inspections`.
  const getAnalyticsData = useCallback(
    () => buildAnalytics(inspections, inspectionChecklist),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inspections]
  );

  // Compress image to reduce storage size
  const compressImage = useCallback((imageSource) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let { width, height } = img;
          const { maxWidth, maxHeight, quality, mimeType } = IMAGE_CONFIG;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          // Create canvas for compression
          const canvas = compressionCanvasRef.current || document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to compressed JPEG
          const compressedData = canvas.toDataURL(mimeType, quality);
          
          // Calculate compression ratio for logging
          const originalSize = imageSource.length;
          const compressedSize = compressedData.length;
          const savings = Math.round((1 - compressedSize / originalSize) * 100);
          console.log(`📸 Image compressed: ${Math.round(originalSize/1024)}KB → ${Math.round(compressedSize/1024)}KB (${savings}% smaller)`);

          resolve(compressedData);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = imageSource;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleaning Inspection Checklist Categories
  const inspectionChecklist = {
    kitchen: {
      label: 'Kitchen',
      icon: '🍳',
      items: [
        { id: 'k1', name: 'Countertops wiped and sanitized', critical: true },
        { id: 'k2', name: 'Stovetop cleaned (no grease/residue)', critical: true },
        { id: 'k3', name: 'Microwave interior/exterior cleaned', critical: false },
        { id: 'k4', name: 'Sink and faucet cleaned and shined', critical: true },
        { id: 'k5', name: 'Cabinet fronts wiped down', critical: false },
        { id: 'k6', name: 'Refrigerator exterior cleaned', critical: false },
        { id: 'k7', name: 'Dishwasher exterior cleaned', critical: false },
        { id: 'k8', name: 'Floor swept and mopped', critical: true },
        { id: 'k9', name: 'Trash emptied and new liner placed', critical: true },
        { id: 'k10', name: 'Light fixtures/switches wiped', critical: false }
      ]
    },
    bathroom: {
      label: 'Bathrooms',
      icon: '🚿',
      items: [
        { id: 'b1', name: 'Toilet cleaned and sanitized (inside/outside)', critical: true },
        { id: 'b2', name: 'Sink and faucet cleaned', critical: true },
        { id: 'b3', name: 'Mirror cleaned (no streaks)', critical: true },
        { id: 'b4', name: 'Shower/tub cleaned and scrubbed', critical: true },
        { id: 'b5', name: 'Shower door/curtain cleaned', critical: false },
        { id: 'b6', name: 'Countertops wiped and sanitized', critical: true },
        { id: 'b7', name: 'Floor mopped', critical: true },
        { id: 'b8', name: 'Trash emptied', critical: true },
        { id: 'b9', name: 'Towels folded/arranged neatly', critical: false },
        { id: 'b10', name: 'Toiletries organized', critical: false }
      ]
    },
    bedroom: {
      label: 'Bedrooms',
      icon: '🛏️',
      items: [
        { id: 'br1', name: 'Bed made properly', critical: true },
        { id: 'br2', name: 'Surfaces dusted (nightstands, dressers)', critical: true },
        { id: 'br3', name: 'Floor vacuumed/mopped', critical: true },
        { id: 'br4', name: 'Mirrors cleaned', critical: false },
        { id: 'br5', name: 'Light fixtures/switches wiped', critical: false },
        { id: 'br6', name: 'Closet doors wiped', critical: false },
        { id: 'br7', name: 'Under bed area cleaned', critical: false },
        { id: 'br8', name: 'Windows/blinds dusted', critical: false }
      ]
    },
    living: {
      label: 'Living Areas',
      icon: '🛋️',
      items: [
        { id: 'l1', name: 'All surfaces dusted', critical: true },
        { id: 'l2', name: 'Floor vacuumed/mopped', critical: true },
        { id: 'l3', name: 'Furniture wiped down', critical: true },
        { id: 'l4', name: 'TV screen cleaned (if applicable)', critical: false },
        { id: 'l5', name: 'Coffee tables cleared and cleaned', critical: false },
        { id: 'l6', name: 'Cushions fluffed/arranged', critical: false },
        { id: 'l7', name: 'Light fixtures/switches wiped', critical: false },
        { id: 'l8', name: 'Baseboards dusted', critical: false },
        { id: 'l9', name: 'Window sills cleaned', critical: false }
      ]
    },
    floors: {
      label: 'Floors & Carpets',
      icon: '🧹',
      items: [
        { id: 'f1', name: 'All hard floors swept', critical: true },
        { id: 'f2', name: 'All hard floors mopped', critical: true },
        { id: 'f3', name: 'Carpets vacuumed thoroughly', critical: true },
        { id: 'f4', name: 'Area rugs vacuumed', critical: false },
        { id: 'f5', name: 'Corners and edges cleaned', critical: false },
        { id: 'f6', name: 'Floor mats cleaned', critical: false },
        { id: 'f7', name: 'No visible debris/dirt', critical: true }
      ]
    },
    windows: {
      label: 'Windows & Glass',
      icon: '🪟',
      items: [
        { id: 'w1', name: 'Interior windows cleaned', critical: false },
        { id: 'w2', name: 'Window sills wiped', critical: false },
        { id: 'w3', name: 'Glass doors cleaned', critical: false },
        { id: 'w4', name: 'Mirrors streak-free', critical: true },
        { id: 'w5', name: 'Blinds/curtains dusted', critical: false }
      ]
    },
    general: {
      label: 'General & Final Check',
      icon: '✨',
      items: [
        { id: 'g1', name: 'All trash removed from property', critical: true },
        { id: 'g2', name: 'Pleasant smell throughout', critical: true },
        { id: 'g3', name: 'No visible cobwebs', critical: true },
        { id: 'g4', name: 'Light switches/door handles sanitized', critical: true },
        { id: 'g5', name: 'Supplies restocked (if applicable)', critical: false },
        { id: 'g6', name: 'Doors/windows locked (if required)', critical: true },
        { id: 'g7', name: 'Cleaning supplies removed', critical: true },
        { id: 'g8', name: 'Overall appearance satisfactory', critical: true }
      ]
    }
  };

  useEffect(() => {
    loadData();
    updateStorageInfo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate IndexedDB storage usage for photos
  const updateStorageInfo = async () => {
    try {
      const info = await photoStorage.getStorageUsed();
      setStorageInfo(info);
    } catch (e) {
      console.error('Error calculating storage:', e);
    }
  };

  // Clear old photos to free up storage
  const handleClearOldPhotos = async () => {
    try {
      const deletedCount = await photoStorage.clearOldPhotos(30);
      toast.success(`Cleared ${deletedCount} photos older than 30 days`);
      updateStorageInfo();
    } catch (e) {
      toast.error('Failed to clear old photos');
    }
  };

  const loadData = async () => {
    // Load appointments, employees, customers from localStorage (they don't have backend yet)
    const appointmentsData = JSON.parse(localStorage.getItem('maidly_appointments') || '[]');
    setAppointments(appointmentsData);
    
    const employeesData = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
    setEmployees(employeesData);
    
    const customersData = JSON.parse(localStorage.getItem('maidly_customers') || '[]');
    setCustomers(customersData);

    // Load inspections from backend API
    try {
      const userId = user?.id || 'default-user';
      const response = await inspectionsApi.list(userId);
      
      if (response.success) {
        // Convert API response to match frontend data format
        const apiInspections = response.inspections.map(insp => ({
          ...insp,
          // Map snake_case to camelCase for frontend compatibility
          appointmentId: insp.appointment_id,
          customerId: insp.customer_id,
          customerName: insp.customer_name,
          serviceType: insp.service_type,
          scheduledDate: insp.scheduled_date,
          assignedEmployees: insp.assigned_employees,
          overallNotes: insp.overall_notes,
          overallRating: insp.overall_rating,
          startedAt: insp.started_at,
          completedAt: insp.completed_at,
          approvedAt: insp.approved_at,
          approvedBy: insp.approved_by,
          customerSignature: insp.customer_signature,
          inspectorSignature: insp.inspector_signature,
          createdAt: insp.created_at
        }));
        setInspections(apiInspections);
        
        // Also sync from appointments
        await syncInspectionsFromAppointments(appointmentsData, userId);
      } else {
        // Fallback to localStorage if API fails
        console.warn('API failed, falling back to localStorage');
        const inspectionsData = JSON.parse(localStorage.getItem('maidly_inspections') || '[]');
        setInspections(inspectionsData);
      }
    } catch (error) {
      console.error('Error loading inspections from API:', error);
      // Fallback to localStorage
      const inspectionsData = JSON.parse(localStorage.getItem('maidly_inspections') || '[]');
      setInspections(inspectionsData);
    }
  };

  const syncInspectionsFromAppointments = async (appts, userId) => {
    try {
      const result = await inspectionsApi.syncFromAppointments(userId, appts);
      if (result.success && result.created > 0) {
        // Reload inspections to get the new ones
        const response = await inspectionsApi.list(userId);
        if (response.success) {
          const apiInspections = response.inspections.map(insp => ({
            ...insp,
            appointmentId: insp.appointment_id,
            customerId: insp.customer_id,
            customerName: insp.customer_name,
            serviceType: insp.service_type,
            scheduledDate: insp.scheduled_date,
            assignedEmployees: insp.assigned_employees,
            overallNotes: insp.overall_notes,
            overallRating: insp.overall_rating,
            startedAt: insp.started_at,
            completedAt: insp.completed_at,
            approvedAt: insp.approved_at,
            approvedBy: insp.approved_by,
            customerSignature: insp.customer_signature,
            inspectorSignature: insp.inspector_signature,
            createdAt: insp.created_at
          }));
          setInspections(apiInspections);
          toast.success(`${result.created} new inspection(s) created from appointments`);
        }
      }
    } catch (error) {
      console.error('Error syncing inspections:', error);
    }
  };

  const createInspectionFromAppointment = (appointment) => {
    // Initialize checklist with all items set to null (not inspected)
    const checklistResults = {};
    Object.keys(inspectionChecklist).forEach(category => {
      checklistResults[category] = {};
      inspectionChecklist[category].items.forEach(item => {
        checklistResults[category][item.id] = {
          status: null, // null = not checked, 'pass', 'fail', 'na'
          notes: '',
          photos: []
        };
      });
    });

    return {
      id: `insp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      appointmentId: appointment.id,
      customerId: appointment.customerId,
      customerName: appointment.customerName,
      address: appointment.address,
      serviceType: appointment.serviceType,
      scheduledDate: appointment.date,
      assignedEmployees: appointment.assignedEmployees,
      status: 'pending', // pending, in_progress, completed, approved
      checklist: checklistResults,
      overallNotes: '',
      overallRating: null, // 1-5 stars
      photos: [],
      startedAt: null,
      completedAt: null,
      approvedAt: null,
      approvedBy: null,
      createdAt: new Date().toISOString()
    };
  };

  const startInspection = async (inspection) => {
    try {
      const response = await inspectionsApi.start(inspection.id);
      if (response.success) {
        const updatedInsp = {
          ...inspection,
          status: 'in_progress',
          startedAt: response.inspection?.started_at || new Date().toISOString()
        };
        
        const updated = inspections.map(i => 
          i.id === inspection.id ? updatedInsp : i
        );
        setInspections(updated);
        setSelectedInspection(updatedInsp);
        setShowInspectionModal(true);
        
        // Expand all categories by default
        const expanded = {};
        Object.keys(inspectionChecklist).forEach(cat => expanded[cat] = true);
        setExpandedCategories(expanded);
      } else {
        toast.error(response.message || 'Failed to start inspection');
      }
    } catch (error) {
      console.error('Error starting inspection:', error);
      // Fallback to local state update
      const updated = inspections.map(i => 
        i.id === inspection.id 
          ? { ...i, status: 'in_progress', startedAt: new Date().toISOString() }
          : i
      );
      setInspections(updated);
      setSelectedInspection({ ...inspection, status: 'in_progress', startedAt: new Date().toISOString() });
      setShowInspectionModal(true);
      
      const expanded = {};
      Object.keys(inspectionChecklist).forEach(cat => expanded[cat] = true);
      setExpandedCategories(expanded);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateChecklistItem = async (category, itemId, field, value) => {
    if (!selectedInspection) return;

    const updatedChecklist = { ...selectedInspection.checklist };
    updatedChecklist[category][itemId] = {
      ...updatedChecklist[category][itemId],
      [field]: value
    };

    const updatedInspection = { ...selectedInspection, checklist: updatedChecklist };
    setSelectedInspection(updatedInspection);

    // Save to state
    const updated = inspections.map(i => 
      i.id === selectedInspection.id ? updatedInspection : i
    );
    setInspections(updated);
    
    // Save to API (debounced)
    saveInspectionToAPI(updatedInspection);
    
    // Update storage info
    updateStorageInfo();
  };

  // ============ QUICK WIN FEATURES ============

  // 1. Pass All / Fail All for a section
  const bulkUpdateSection = (category, status) => {
    if (!selectedInspection) return;
    
    const updatedChecklist = { ...selectedInspection.checklist };
    const items = inspectionChecklist[category].items;
    
    items.forEach(item => {
      updatedChecklist[category][item.id] = {
        ...updatedChecklist[category][item.id],
        status: status
      };
    });
    
    const updatedInspection = { ...selectedInspection, checklist: updatedChecklist };
    setSelectedInspection(updatedInspection);
    
    const updated = inspections.map(i => 
      i.id === selectedInspection.id ? updatedInspection : i
    );
    setInspections(updated);
    
    // Save to API
    saveInspectionToAPI(updatedInspection);
    
    toast.success(`All items in ${inspectionChecklist[category].label} marked as ${status.toUpperCase()}`);
  };

  // 2. Get section status counts
  // Thin wrapper that binds the page's local checklist schema to the
  // pure helper in /utils/inspectionScoring.js.
  const getSectionCounts = (category) =>
    getCountsForCategory(selectedInspection, inspectionChecklist, category);

  // 4. Keyboard shortcuts handler
  useEffect(() => {
    if (!showInspectionModal || !focusedItemId) return;
    
    const handleKeyPress = (e) => {
      // Only handle if not typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const [category, itemId] = focusedItemId.split('|');
      if (!category || !itemId) return;
      
      switch (e.key.toLowerCase()) {
        case 'p':
          updateChecklistItem(category, itemId, 'status', 'pass');
          toast.success('Marked as PASS (P)');
          break;
        case 'f':
          updateChecklistItem(category, itemId, 'status', 'fail');
          toast.success('Marked as FAIL (F)');
          break;
        case 'n':
          updateChecklistItem(category, itemId, 'status', 'na');
          toast.success('Marked as N/A (N)');
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showInspectionModal, focusedItemId, updateChecklistItem]);

  // 5. Filter items by critical status
  const getFilteredItems = (category) => {
    const items = inspectionChecklist[category].items;
    if (!showCriticalOnly) return items;
    return items.filter(item => item.critical);
  };

  // ============ END QUICK WIN FEATURES ============

  // Save photo to IndexedDB for extended storage
  const savePhotoToStorage = async (photoData, inspectionId, category, itemId) => {
    const photoRecord = {
      id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      inspectionId,
      category,
      itemId,
      data: photoData,
      timestamp: new Date().toISOString()
    };
    
    await photoStorage.save(photoRecord);
    return photoRecord.id;
  };

  // Camera Functions
  const startCamera = useCallback(async () => {
    try {
      // Stop any existing stream
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setCameraStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera error:', err);
      toast.error('Could not access camera. Please check permissions.');
      // Fall back to file input
      fileInputRef.current?.click();
      setShowCameraModal(false);
    }
  }, [facingMode, cameraStream]);

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCapturedImage(null);
  }, [cameraStream]);

  const switchCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  useEffect(() => {
    if (showCameraModal && !capturedImage) {
      startCamera();
    }
    return () => {
      if (!showCameraModal) {
        stopCamera();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCameraModal, facingMode, capturedImage]);

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get raw image data
    const rawImageData = canvas.toDataURL('image/jpeg', 0.9);
    
    // Stop camera stream
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }

    // Compress the image
    setIsCompressing(true);
    try {
      const compressedImage = await compressImage(rawImageData);
      setCapturedImage(compressedImage);
    } catch (err) {
      console.error('Compression failed, using original:', err);
      setCapturedImage(rawImageData);
    } finally {
      setIsCompressing(false);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const savePhoto = async () => {
    if (!capturedImage || !currentPhotoItem) return;

    const { category, itemId } = currentPhotoItem;
    
    try {
      // Save photo to IndexedDB for extended storage
      const photoId = await savePhotoToStorage(
        capturedImage, 
        selectedInspection.id, 
        category, 
        itemId
      );
      
      // Store photo with metadata including location and mode
      const currentPhotos = selectedInspection.checklist[category][itemId].photos || [];
      const photoData = {
        id: photoId,
        data: capturedImage,
        timestamp: new Date().toISOString(),
        storedInDB: true,
        mode: photoMode, // 'standard', 'before', or 'after'
        location: currentLocation // GPS coordinates if available
      };
      
      updateChecklistItem(category, itemId, 'photos', [...currentPhotos, photoData]);
      
      const modeLabel = photoMode === 'before' ? 'Before photo' : photoMode === 'after' ? 'After photo' : 'Photo';
      toast.success(`${modeLabel} saved with GPS!`);
      updateStorageInfo();
    } catch (err) {
      console.error('Failed to save to IndexedDB:', err);
      // Fallback to localStorage
      const currentPhotos = selectedInspection.checklist[category][itemId].photos || [];
      updateChecklistItem(category, itemId, 'photos', [...currentPhotos, {
        id: Date.now(),
        data: capturedImage,
        timestamp: new Date().toISOString(),
        mode: photoMode,
        location: currentLocation
      }]);
      toast.success('Photo saved!');
    }
    
    closeCameraModal();
  };

  const closeCameraModal = () => {
    stopCamera();
    setCapturedImage(null);
    setCurrentPhotoItem(null);
    setShowCameraModal(false);
    setPhotoMode('standard');
  };

  const handlePhotoCapture = (category, itemId, mode = 'standard') => {
    setCurrentPhotoItem({ category, itemId });
    setPhotoMode(mode);
    getCurrentLocation(); // Get GPS location when taking photo
    
    // Check if camera is available
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      setShowCameraModal(true);
    } else {
      // Fall back to file input
      fileInputRef.current?.click();
    }
  };

  // Signature functions
  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const saveSignature = () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Please provide a signature first');
      return;
    }
    
    const signatureData = signatureRef.current.toDataURL('image/png');
    
    if (signatureType === 'customer') {
      const updated = { ...selectedInspection, customerSignature: signatureData, customerSignedAt: new Date().toISOString() };
      setSelectedInspection(updated);
      const all = inspections.map(i => i.id === updated.id ? updated : i);
      setInspections(all);
      saveInspectionToAPI(updated);
      toast.success('Customer signature saved!');
    } else {
      const updated = { ...selectedInspection, inspectorSignature: signatureData, inspectorSignedAt: new Date().toISOString() };
      setSelectedInspection(updated);
      const all = inspections.map(i => i.id === updated.id ? updated : i);
      setInspections(all);
      saveInspectionToAPI(updated);
      toast.success('Inspector signature saved!');
    }
    
    setShowSignatureModal(false);
  };

  // Email report function
  const sendEmailReport = async (inspection) => {
    if (!emailAddress || !emailAddress.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setEmailSending(true);
    
    try {
      // Generate PDF as base64
      const pdfDoc = generatePDFReportData(inspection);
      
      // For now, open mailto link with report info
      // In production, this would send via backend API
      const subject = encodeURIComponent(`Cleaning Inspection Report - ${inspection.customerName}`);
      const body = encodeURIComponent(`
Dear ${inspection.customerName},

Please find attached your Cleaning Inspection Report.

Inspection Details:
- Date: ${new Date(inspection.scheduledDate).toLocaleDateString()}
- Address: ${inspection.address}
- Status: ${inspection.status.toUpperCase()}
- Score: ${calculateScore(inspection)}%

Thank you for choosing our services!

Best regards,
Maidly Cleaning Team
      `);
      
      window.open(`mailto:${emailAddress}?subject=${subject}&body=${body}`, '_blank');
      
      toast.success('Email client opened! Please attach the PDF report.');
      setShowEmailModal(false);
      setEmailAddress('');
    } catch (err) {
      console.error('Email error:', err);
      toast.error('Failed to prepare email');
    } finally {
      setEmailSending(false);
    }
  };

  // Generate PDF data (without saving)
  const generatePDFReportData = (inspection) => {
    const score = calculateScore(inspection);
    return { inspection, score };
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentPhotoItem) return;

    // Show loading state
    setIsCompressing(true);
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        // Compress the uploaded image
        const compressedImage = await compressImage(reader.result);
        
        const { category, itemId } = currentPhotoItem;
        
        // Save to IndexedDB for extended storage
        const photoId = await savePhotoToStorage(
          compressedImage,
          selectedInspection.id,
          category,
          itemId
        );
        
        const currentPhotos = selectedInspection.checklist[category][itemId].photos || [];
        updateChecklistItem(category, itemId, 'photos', [...currentPhotos, {
          id: photoId,
          data: compressedImage,
          timestamp: new Date().toISOString(),
          storedInDB: true
        }]);
        toast.success('Photo added to extended storage!');
        updateStorageInfo();
      } catch (err) {
        console.error('Storage failed:', err);
        // Fallback to localStorage
        const { category, itemId } = currentPhotoItem;
        const currentPhotos = selectedInspection.checklist[category][itemId].photos || [];
        updateChecklistItem(category, itemId, 'photos', [...currentPhotos, {
          id: Date.now(),
          data: reader.result,
          timestamp: new Date().toISOString()
        }]);
        toast.success('Photo added');
      } finally {
        setIsCompressing(false);
        setCurrentPhotoItem(null);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removePhoto = async (category, itemId, photoId) => {
    // Try to remove from IndexedDB
    try {
      await photoStorage.delete(photoId);
    } catch (e) {
      console.log('Photo not in IndexedDB or already deleted');
    }
    
    const currentPhotos = selectedInspection.checklist[category][itemId].photos || [];
    updateChecklistItem(category, itemId, 'photos', currentPhotos.filter(p => p.id !== photoId));
    updateStorageInfo();
    toast.success('Photo removed');
  };

  const calculateProgress = (inspection) => calcProgress(inspection, inspectionChecklist);

  const calculateScore = (inspection) => calcScore(inspection, inspectionChecklist);

  // Save inspection to API (debounced)
  const saveInspectionToAPI = async (inspection) => {
    try {
      const userId = user?.id || 'default-user';
      await inspectionsApi.update(inspection.id, {
        status: inspection.status,
        checklist: inspection.checklist,
        overall_notes: inspection.overallNotes,
        overall_rating: inspection.overallRating,
        photos: inspection.photos,
        started_at: inspection.startedAt,
        completed_at: inspection.completedAt,
        approved_at: inspection.approvedAt,
        approved_by: inspection.approvedBy,
        customer_signature: inspection.customerSignature,
        inspector_signature: inspection.inspectorSignature,
        location: inspection.location
      });
    } catch (error) {
      console.error('Error saving inspection to API:', error);
    }
  };

  const completeInspection = async () => {
    if (!selectedInspection) return;

    const progress = calculateProgress(selectedInspection);
    if (progress < 100) {
      toast.error('Please complete all inspection items before finishing');
      return;
    }

    const score = calculateScore(selectedInspection);
    const updatedInspection = {
      ...selectedInspection,
      status: 'completed',
      completedAt: new Date().toISOString(),
      overallRating: scoreToRating(score)
    };

    const updated = inspections.map(i => 
      i.id === selectedInspection.id ? updatedInspection : i
    );
    setInspections(updated);
    setSelectedInspection(updatedInspection);
    
    // Save to API
    try {
      await inspectionsApi.complete(selectedInspection.id);
      await saveInspectionToAPI(updatedInspection);
      toast.success('Inspection completed!');
    } catch (error) {
      console.error('Error completing inspection:', error);
      toast.success('Inspection completed locally!');
    }
  };

  const approveInspection = async (inspection) => {
    const approverEmail = user?.email || 'Manager';
    const updated = inspections.map(i => 
      i.id === inspection.id 
        ? { ...i, status: 'approved', approvedAt: new Date().toISOString(), approvedBy: approverEmail }
        : i
    );
    setInspections(updated);
    
    // Save to API
    try {
      await inspectionsApi.approve(inspection.id, approverEmail);
      toast.success('Inspection approved!');
    } catch (error) {
      console.error('Error approving inspection:', error);
      toast.success('Inspection approved locally!');
    }
  };

  const editInspection = (inspection) => {
    setSelectedInspection(inspection);
    setShowInspectionModal(true);
    // Expand all categories
    const expanded = {};
    Object.keys(inspectionChecklist).forEach(cat => expanded[cat] = true);
    setExpandedCategories(expanded);
  };

  const deleteInspection = async (inspectionId) => {
    const updated = inspections.filter(i => i.id !== inspectionId);
    setInspections(updated);
    setShowDeleteConfirm(null);
    
    // Delete from API
    try {
      await inspectionsApi.delete(inspectionId);
      toast.success('Inspection deleted!');
    } catch (error) {
      console.error('Error deleting inspection:', error);
      toast.success('Inspection deleted locally!');
    }
  };

  const generatePDFReport = (inspection) => {
    const score = calculateScore(inspection);
    const inspectorName = employees.find(e => inspection.assignedEmployees?.includes(e.id))?.name || 'N/A';
    
    // Create PDF document
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Colors
    const primaryColor = [139, 92, 246];
    const successColor = [34, 197, 94];
    const dangerColor = [239, 68, 68];
    const grayColor = [107, 114, 128];
    
    // Header
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('CLEANING INSPECTION REPORT', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Quality Assurance Documentation', pageWidth / 2, 30, { align: 'center' });
    
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 40, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    
    // Customer Info Box
    let yPos = 55;
    doc.setFillColor(249, 250, 251);
    doc.roundedRect(14, yPos, pageWidth - 28, 35, 3, 3, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Customer Information', 20, yPos + 10);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...grayColor);
    doc.text(`Customer: ${inspection.customerName}`, 20, yPos + 20);
    doc.text(`Address: ${inspection.address}`, 20, yPos + 28);
    doc.text(`Date: ${new Date(inspection.scheduledDate).toLocaleDateString()}`, pageWidth / 2, yPos + 20);
    doc.text(`Inspector: ${inspectorName}`, pageWidth / 2, yPos + 28);
    
    // Score Box
    yPos = 100;
    const scoreBoxWidth = 60;
    const scoreBoxX = (pageWidth - scoreBoxWidth) / 2;
    
    if (score >= 80) {
      doc.setFillColor(...successColor);
    } else if (score >= 60) {
      doc.setFillColor(251, 191, 36);
    } else {
      doc.setFillColor(...dangerColor);
    }
    
    doc.roundedRect(scoreBoxX, yPos, scoreBoxWidth, 30, 3, 3, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(`${score}%`, pageWidth / 2, yPos + 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('OVERALL SCORE', pageWidth / 2, yPos + 25, { align: 'center' });
    
    // Status
    doc.setTextColor(0, 0, 0);
    yPos = 140;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Status: ${inspection.status.toUpperCase()}`, 20, yPos);
    
    // Results Header
    yPos = 155;
    doc.setFillColor(...primaryColor);
    doc.rect(14, yPos, pageWidth - 28, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text('DETAILED INSPECTION RESULTS', pageWidth / 2, yPos + 7, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    yPos = 170;
    
    // Draw each category with simple text (no autoTable)
    Object.entries(inspectionChecklist).forEach(([categoryKey, category]) => {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }
      
      // Category Header
      doc.setFillColor(243, 244, 246);
      doc.rect(14, yPos, pageWidth - 28, 8, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(55, 65, 81);
      // Remove emoji icons - jsPDF doesn't support emojis well
      doc.text(category.label.toUpperCase(), 20, yPos + 6);
      yPos += 12;
      
      // Items
      category.items.forEach(item => {
        if (yPos > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }
        
        const result = inspection.checklist[categoryKey]?.[item.id];
        let status = '[ ]';
        
        if (result?.status === 'pass') {
          doc.setTextColor(...successColor);
          status = '[PASS]';
        } else if (result?.status === 'fail') {
          doc.setTextColor(...dangerColor);
          status = '[FAIL]';
        } else if (result?.status === 'na') {
          doc.setTextColor(...grayColor);
          status = '[N/A]';
        } else {
          doc.setTextColor(...grayColor);
        }
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(status, 20, yPos);
        
        doc.setTextColor(55, 65, 81);
        doc.setFont('helvetica', 'normal');
        const itemText = item.name + (item.critical ? ' *' : '');
        doc.text(itemText, 42, yPos);
        
        if (result?.notes) {
          doc.setTextColor(...grayColor);
          doc.setFontSize(8);
          doc.text(`Note: ${result.notes}`, 28, yPos + 4);
          yPos += 4;
        }
        
        yPos += 6;
      });
      
      yPos += 5;
    });
    
    // Overall Notes
    if (inspection.overallNotes) {
      if (yPos > pageHeight - 50) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFillColor(254, 243, 199);
      doc.roundedRect(14, yPos, pageWidth - 28, 30, 3, 3, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(146, 64, 14);
      doc.text('Inspector Notes:', 20, yPos + 10);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(55, 65, 81);
      const splitNotes = doc.splitTextToSize(inspection.overallNotes, pageWidth - 40);
      doc.text(splitNotes, 20, yPos + 18);
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...grayColor);
      doc.text(
        `Page ${i} of ${pageCount} | Maidly Business Manager`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }
    
    // Save
    const fileName = `Inspection-Report-${inspection.customerName.replace(/\s+/g, '-')}-${inspection.scheduledDate}.pdf`;
    doc.save(fileName);
    toast.success('PDF Report downloaded!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In Progress' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
      approved: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Approved' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  const filteredInspections = inspections.filter(inspection => {
    // For employees, only show their assigned inspections
    if (user?.role === 'employee') {
      if (!inspection.assignedEmployees?.includes(user.id)) return false;
    }
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!inspection.customerName?.toLowerCase().includes(search) &&
          !inspection.address?.toLowerCase().includes(search)) {
        return false;
      }
    }
    
    // Status filter
    if (filterStatus !== 'all' && inspection.status !== filterStatus) {
      return false;
    }
    
    return true;
  }).sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate));

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <div className="inspection-page" style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconTile icon={ClipboardCheck} tint={TINT.inspections} size={36} />
            Cleaning Inspections
          </h1>
          <p style={{ color: '#6b7280', marginTop: '4px' }}>
            Quality control for cleaning services
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Tab Switcher — same visual language as Time Clock / Employees tabs */}
          <div
            role="tablist"
            aria-label="Inspection views"
            style={{
              display: 'inline-flex', gap: 6, padding: 5,
              background: '#f1f5f9', borderRadius: 12, border: '1px solid #e2e8f0',
              boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.04)',
            }}
          >
            {[
              { id: 'queue', label: 'Queue', icon: ClipboardCheck, tint: TINT.inspections || '#10b981' },
              { id: 'analytics', label: 'Analytics', icon: BarChart3, tint: '#3b82f6' },
            ].map((t) => {
              const isActive = activeTab === t.id;
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`inspection-tab-${t.id}`}
                  onClick={() => setActiveTab(t.id)}
                  style={{
                    all: 'unset', cursor: 'pointer',
                    padding: '8px 18px 8px 8px', borderRadius: 9,
                    fontSize: 13, fontWeight: 700, letterSpacing: 0.1,
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: isActive ? '#0073ea' : 'transparent',
                    color: isActive ? '#ffffff' : '#475569',
                    boxShadow: isActive ? '0 3px 10px rgba(0,115,234,0.28)' : 'none',
                    transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(15,23,42,0.05)'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <IconTile icon={Icon} tint={isActive ? 'rgba(255,255,255,0.22)' : t.tint} size={24} iconSize={13} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'queue' && (
            <>
              {/* Search */}
              <div style={{ position: 'relative' }}>
                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Search inspections..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: '10px 12px 10px 40px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    width: '220px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Filter */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="approved">Approved</option>
              </select>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards — soft IconTile style, matches Settings/Storage design language */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {[
          { icon: Clock, label: 'Pending', value: inspections.filter(i => i.status === 'pending').length, tint: '#f59e0b' },
          { icon: Activity, label: 'In Progress', value: inspections.filter(i => i.status === 'in_progress').length, tint: '#0073ea' },
          { icon: CheckCircle2, label: 'Completed', value: inspections.filter(i => i.status === 'completed' || i.status === 'approved').length, tint: '#22c55e' },
          { icon: TrendingUp, label: 'Avg Score', value: (() => {
              const scores = inspections
                .filter(i => i.status === 'completed' || i.status === 'approved')
                .map(i => calculateScore(i))
                .filter(s => s !== null);
              return scores.length > 0 ? `${Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)}%` : '—';
            })(), tint: '#8b5cf6' },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              padding: '18px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              boxShadow: '0 1px 2px rgba(15,23,42,0.03)',
            }}
          >
            <IconTile icon={stat.icon} tint={stat.tint} size={44} iconSize={20} />
            <div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
                {stat.value}
              </div>
              <div style={{ color: '#64748b', fontSize: '13px', fontWeight: 500, marginTop: '4px' }}>
                {stat.label}
              </div>
            </div>
          </div>
        ))}
        {/* Extended Storage Usage Indicator - 100MB with IndexedDB */}
        <div style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          padding: '18px',
          borderRadius: '12px',
          position: 'relative',
          boxShadow: '0 1px 2px rgba(15,23,42,0.03)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <IconTile icon={HardDrive} tint="#0ea5e9" size={38} iconSize={18} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: 0.4 }}>Photo Storage</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: storageInfo.size > 80000000 ? '#dc2626' : '#0f172a', lineHeight: 1 }}>
                {storageInfo.size < 1024
                  ? `${storageInfo.size} B`
                  : storageInfo.size < 1024 * 1024
                    ? `${(storageInfo.size / 1024).toFixed(1)} KB`
                    : `${(storageInfo.size / (1024 * 1024)).toFixed(1)} MB`}
              </div>
              <div style={{ color: '#64748b', fontWeight: 500, fontSize: 12, marginTop: 2 }}>
                {storageInfo.count} photos
              </div>
            </div>
          </div>
          <div style={{
            height: '6px',
            background: '#e2e8f0',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${Math.min((storageInfo.size / STORAGE_LIMIT) * 100, 100)}%`,
              background: storageInfo.size > 80000000 ? '#dc2626' : storageInfo.size > 50000000 ? '#f59e0b' : '#22c55e',
              transition: 'width 0.3s'
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
            <span style={{ fontSize: '11px', color: '#64748b' }}>
              {Math.round((storageInfo.size / STORAGE_LIMIT) * 100)}% of 100MB
            </span>
            {storageInfo.count > 0 && (
              <button
                onClick={handleClearOldPhotos}
                style={{
                  fontSize: '11px',
                  color: '#64748b',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <RefreshCw size={10} /> Clear old
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {activeTab === 'analytics' && (
        <InspectionAnalyticsTab analytics={getAnalyticsData()} />
      )}

      {/* Inspections List - Only show when on queue tab */}
      {activeTab === 'queue' && (
        <InspectionList
          filteredInspections={filteredInspections}
          inspectionChecklist={inspectionChecklist}
          user={user}
          calculateProgress={calculateProgress}
          calculateScore={calculateScore}
          getStatusBadge={getStatusBadge}
          startInspection={startInspection}
          approveInspection={approveInspection}
          editInspection={editInspection}
          generatePDFReport={generatePDFReport}
          setSelectedInspection={setSelectedInspection}
          setShowInspectionModal={setShowInspectionModal}
          setExpandedCategories={setExpandedCategories}
          setShowReportPreview={setShowReportPreview}
          setEmailAddress={setEmailAddress}
          setShowEmailModal={setShowEmailModal}
          setShowDeleteConfirm={setShowDeleteConfirm}
        />
      )}

      {/* Email Report Modal */}
      {showEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '450px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #dbeafe 0%, #93c5fd 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Mail size={24} color="#3b82f6" />
              </div>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>Email Report</h3>
                <p style={{ fontSize: '13px', color: '#6b7280' }}>Send inspection report to customer</p>
              </div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#374151' }}>
                Recipient Email
              </label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="customer@example.com"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            {selectedInspection && (
              <div style={{ 
                background: '#f9fafb', 
                padding: '12px', 
                borderRadius: '8px', 
                marginBottom: '16px',
                fontSize: '13px',
                color: '#6b7280'
              }}>
                <div><strong>Customer:</strong> {selectedInspection.customerName}</div>
                <div><strong>Date:</strong> {new Date(selectedInspection.scheduledDate).toLocaleDateString()}</div>
                <div><strong>Score:</strong> {calculateScore(selectedInspection)}%</div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowEmailModal(false); setEmailAddress(''); }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => sendEmailReport(selectedInspection)}
                disabled={emailSending}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: emailSending ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: emailSending ? 0.7 : 1
                }}
              >
                {emailSending ? 'Preparing...' : <><Send size={16} /> Send Email</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Trash size={28} color="#dc2626" />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>
              Delete Inspection?
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '14px' }}>
              This action cannot be undone. The inspection record will be permanently removed.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                style={{
                  padding: '10px 24px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteInspection(showDeleteConfirm)}
                style={{
                  padding: '10px 24px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Trash size={16} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for photo capture */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        capture="environment"
        onChange={handlePhotoUpload}
        style={{ display: 'none' }}
      />

      {/* Inspection Modal */}
      <InspectionModal
        showInspectionModal={showInspectionModal}
        selectedInspection={selectedInspection}
        setSelectedInspection={setSelectedInspection}
        setShowInspectionModal={setShowInspectionModal}
        focusedItemId={focusedItemId}
        setFocusedItemId={setFocusedItemId}
        showCriticalOnly={showCriticalOnly}
        setShowCriticalOnly={setShowCriticalOnly}
        expandedCategories={expandedCategories}
        inspectionChecklist={inspectionChecklist}
        inspections={inspections}
        setInspections={setInspections}
        updateChecklistItem={updateChecklistItem}
        calculateProgress={calculateProgress}
        calculateScore={calculateScore}
        getFilteredItems={getFilteredItems}
        getSectionCounts={getSectionCounts}
        handlePhotoCapture={handlePhotoCapture}
        removePhoto={removePhoto}
        completeInspection={completeInspection}
        saveInspectionToAPI={saveInspectionToAPI}
        toggleCategory={toggleCategory}
        bulkUpdateSection={bulkUpdateSection}
        setShowSignatureModal={setShowSignatureModal}
        setSignatureType={setSignatureType}
      />

      {/* Report Preview Modal */}
      {showReportPreview && selectedInspection && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '20px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700' }}>Inspection Report</h2>
              <button onClick={() => setShowReportPreview(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            
            <div style={{ padding: '20px' }}>
              {/* Report Header */}
              <div style={{ textAlign: 'center', marginBottom: '24px', paddingBottom: '24px', borderBottom: '2px solid #e5e7eb' }}>
                <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>CLEANING INSPECTION REPORT</h1>
                <p style={{ color: '#6b7280' }}>{selectedInspection.customerName}</p>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>{selectedInspection.address}</p>
                <div style={{ 
                  marginTop: '16px',
                  display: 'inline-block',
                  padding: '12px 24px',
                  background: calculateScore(selectedInspection) >= 80 ? '#d1fae5' : '#fef2f2',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: '700', color: calculateScore(selectedInspection) >= 80 ? '#059669' : '#dc2626' }}>
                    {calculateScore(selectedInspection)}%
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Overall Score</div>
                </div>
              </div>

              {/* Results by Category */}
              {Object.entries(inspectionChecklist).map(([categoryKey, category]) => (
                <div key={categoryKey} style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {category.icon} {category.label}
                  </h3>
                  <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px' }}>
                    {category.items.map(item => {
                      const result = selectedInspection.checklist[categoryKey]?.[item.id];
                      return (
                        <div key={item.id} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          padding: '8px 0',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <span style={{ color: '#374151' }}>{item.name}</span>
                          <span style={{ 
                            fontWeight: '600',
                            color: result?.status === 'pass' ? '#059669' : result?.status === 'fail' ? '#dc2626' : '#6b7280'
                          }}>
                            {result?.status === 'pass' ? '✓ Pass' : result?.status === 'fail' ? '✗ Fail' : '- N/A'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Overall Notes */}
              {selectedInspection.overallNotes && (
                <div style={{ marginTop: '24px', padding: '16px', background: '#f3f4f6', borderRadius: '8px' }}>
                  <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>Inspector Notes</h3>
                  <p style={{ color: '#374151' }}>{selectedInspection.overallNotes}</p>
                </div>
              )}

              {/* Footer */}
              <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', fontSize: '12px', color: '#9ca3af', textAlign: 'center' }}>
                <p>Inspection Date: {new Date(selectedInspection.scheduledDate).toLocaleDateString()}</p>
                <p>Completed: {selectedInspection.completedAt ? new Date(selectedInspection.completedAt).toLocaleString() : 'N/A'}</p>
                {selectedInspection.approvedBy && <p>Approved by: {selectedInspection.approvedBy}</p>}
              </div>
            </div>

            <div style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => generatePDFReport(selectedInspection)}
                style={{
                  padding: '10px 20px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Download size={18} /> Download Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Modal */}
      <InspectionCameraModal
        open={showCameraModal}
        capturedImage={capturedImage}
        cameraStream={cameraStream}
        isCompressing={isCompressing}
        videoRef={videoRef}
        onClose={closeCameraModal}
        onSwitchCamera={switchCamera}
        onCapture={capturePhoto}
        onRetake={retakePhoto}
        onSave={savePhoto}
        onOpenGallery={() => {
          setShowCameraModal(false);
          fileInputRef.current?.click();
        }}
      />


      {/* Hidden canvases for photo capture and compression */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <canvas ref={compressionCanvasRef} style={{ display: 'none' }} />

      {/* Signature Modal */}
      <SignatureModal
        open={showSignatureModal}
        signatureType={signatureType}
        signatureRef={signatureRef}
        onClear={clearSignature}
        onCancel={() => setShowSignatureModal(false)}
        onSave={saveSignature}
      />


      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CleaningInspection;
